import React, { useEffect, useState } from 'react'
import { ResourceUsageCard } from '../../components/client/ResourceUsageCard'
import { QuickActionCard } from '../../components/client/QuickActionCard'
import { HelpTooltip } from '../../components/client/HelpTooltip'
import { clientAPI, domainsAPI } from '@hpanel/web-common/api/client'
import type { ClientOverview, Domain } from '@hpanel/web-common/api/types'

import { useAuth } from '@hpanel/web-common/contexts/AuthContext'

/**
 * Client Dashboard page component.
 *
 * Main landing page for client users.
 * Displays:
 * - Welcome header
 * - Resource usage cards (disk, bandwidth, databases)
 * - Quick action cards (add domain, create database, manage files, check email)
 * - Domain list overview
 */

export const ClientDashboard: React.FC = () => {
  const { user } = useAuth()
  const [overview, setOverview] = useState<ClientOverview | null>(null)
  const [domains, setDomains] = useState<Domain[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        const [overviewData, domainsData] = await Promise.all([
          clientAPI.overview(user?.id),
          domainsAPI.list(user?.id),
        ])
        setOverview(overviewData)
        setDomains(domainsData.domains)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  return (
    <div data-testid="client-dashboard-page" className="space-y-6">
      {/* Page header */}
      <div>
        <h1 data-testid="page-title" className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
          Welcome to Your Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Monitor your resources and manage your hosting
        </p>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Resource usage section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Resource Usage
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="relative">
            <ResourceUsageCard
              title="Disk Usage"
              used={Math.round((overview?.total_disk_usage_bytes ?? 0) / 1048576)}
              total={1024}
              unit="MB"
              icon="💾"
              isLoading={isLoading}
            />
            <span className="absolute top-3 right-3">
              <HelpTooltip text="Total disk space used by your websites, emails, and databases" position="left" />
            </span>
          </div>
          <div className="relative">
            <ResourceUsageCard
              title="Bandwidth"
              used={Math.round((overview?.total_bandwidth_usage_bytes ?? 0) / 1048576)}
              total={10240}
              unit="MB"
              icon="📡"
              isLoading={isLoading}
            />
            <span className="absolute top-3 right-3">
              <HelpTooltip text="Monthly data transfer used by visitor traffic to your sites" position="left" />
            </span>
          </div>
          <div className="relative">
            <ResourceUsageCard
              title="Databases"
              used={overview?.total_databases ?? 0}
              total={10}
              unit="databases"
              icon="🗄️"
              isLoading={isLoading}
            />
            <span className="absolute top-3 right-3">
              <HelpTooltip text="Number of MySQL or PostgreSQL databases created on your account" position="left" />
            </span>
          </div>
        </div>
      </div>

      {/* Quick actions section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionCard
            title="Request a Site"
            description="Ask for a site on a domain you own"
            icon="🌐"
            actionLabel="Request"
            href="/client/sites/request"
          />
          <QuickActionCard
            title="Create Database"
            description="Set up a new MySQL database"
            icon="🗄️"
            actionLabel="Create"
            href="/client/databases/new"
          />
          <QuickActionCard
            title="Manage Files"
            description="Browse and manage your website files"
            icon="📁"
            actionLabel="Open"
            href="/client/files"
          />
          <QuickActionCard
            title="Check Email"
            description="Access your email accounts"
            icon="📧"
            actionLabel="Open"
            href="/client/email"
          />
        </div>
      </div>

      {/* Domain list section */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Your Domains
        </h2>
        {isLoading ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
              ))}
            </div>
          </div>
        ) : domains.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No domains yet. Request your first site to get started.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden overflow-x-auto">
            <table className="w-full min-w-[480px]">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300">Domain</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300">DNS Zone</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300">Disk Usage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {domains.map((domain) => (
                  <tr key={domain.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {domain.name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          domain.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}
                      >
                        {domain.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {domain.dns_zone ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {Math.round((domain.disk_usage_bytes ?? 0) / 1048576)} MB
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
